export type FontRegionGroup = { region: string; families: string[] }

export type GroupedFonts = { main: string[]; regions: FontRegionGroup[] }

export const FONT_REGION_ORDER = [
  'East Asian',
  'South Asian',
  'Southeast Asian',
  'Middle Eastern',
  'African',
  'European & Historic',
  'Americas',
] as const

const NON_SCRIPT_NOTO_TOKENS: ReadonlySet<string> = new Set([
  'Display',
  'Mono',
  'Math',
  'Symbols',
  'Symbols 2',
])

const SCRIPT_TO_REGION: Readonly<Record<string, string>> = {
  HK: 'East Asian',
  JP: 'East Asian',
  KR: 'East Asian',
  SC: 'East Asian',
  TC: 'East Asian',
  'CJK Ext': 'East Asian',
  Miao: 'East Asian',
  Yi: 'East Asian',
  Tibetan: 'East Asian',
  Marchen: 'East Asian',
  Mongolian: 'East Asian',
  'Phags Pa': 'East Asian',
  Lisu: 'East Asian',
  Bengali: 'South Asian',
  Devanagari: 'South Asian',
  Gujarati: 'South Asian',
  Gurmukhi: 'South Asian',
  Kannada: 'South Asian',
  Malayalam: 'South Asian',
  Oriya: 'South Asian',
  Tamil: 'South Asian',
  'Tamil Supplement': 'South Asian',
  Telugu: 'South Asian',
  Sinhala: 'South Asian',
  Grantha: 'South Asian',
  Chakma: 'South Asian',
  Kaithi: 'South Asian',
  Khojki: 'South Asian',
  Khudawadi: 'South Asian',
  Lepcha: 'South Asian',
  Limbu: 'South Asian',
  Mahajani: 'South Asian',
  'Meetei Mayek': 'South Asian',
  Modi: 'South Asian',
  Mro: 'South Asian',
  Multani: 'South Asian',
  'Nag Mundari': 'South Asian',
  Newa: 'South Asian',
  Saurashtra: 'South Asian',
  Sharada: 'South Asian',
  'Sora Sompeng': 'South Asian',
  'Syloti Nagri': 'South Asian',
  Takri: 'South Asian',
  Tangsa: 'South Asian',
  Tirhuta: 'South Asian',
  Wancho: 'South Asian',
  Thaana: 'South Asian',
  'Hanifi Rohingya': 'South Asian',
  Thai: 'Southeast Asian',
  Lao: 'Southeast Asian',
  Khmer: 'Southeast Asian',
  Myanmar: 'Southeast Asian',
  Balinese: 'Southeast Asian',
  Batak: 'Southeast Asian',
  Buginese: 'Southeast Asian',
  Buhid: 'Southeast Asian',
  Cham: 'Southeast Asian',
  Hanunoo: 'Southeast Asian',
  Javanese: 'Southeast Asian',
  'Kayah Li': 'Southeast Asian',
  'New Tai Lue': 'Southeast Asian',
  'Pahawh Hmong': 'Southeast Asian',
  Rejang: 'Southeast Asian',
  Sundanese: 'Southeast Asian',
  Tagalog: 'Southeast Asian',
  Tagbanwa: 'Southeast Asian',
  'Tai Le': 'Southeast Asian',
  'Tai Tham': 'Southeast Asian',
  'Tai Viet': 'Southeast Asian',
  Arabic: 'Middle Eastern',
  Hebrew: 'Middle Eastern',
  Syriac: 'Middle Eastern',
  Mandaic: 'Middle Eastern',
  Adlam: 'African',
  Bamum: 'African',
  'Bassa Vah': 'African',
  Coptic: 'African',
  Ethiopic: 'African',
  'Mende Kikakui': 'African',
  NKo: 'African',
  Osmanya: 'African',
  Tifinagh: 'African',
  Vai: 'African',
  'Canadian Aboriginal': 'Americas',
  Cherokee: 'Americas',
  Osage: 'Americas',
  Deseret: 'Americas',
  Armenian: 'European & Historic',
  Georgian: 'European & Historic',
  Vithkuqi: 'European & Historic',
  'Old Italic': 'European & Historic',
  Runic: 'European & Historic',
  Glagolitic: 'European & Historic',
  Gothic: 'European & Historic',
  Ugaritic: 'European & Historic',
  Ogham: 'European & Historic',
  'Old Persian': 'European & Historic',
  Phoenician: 'European & Historic',
}

export const notoScriptToken = (family: string): string | null => {
  const match = /^Noto (?:Sans|Serif) (.+)$/.exec(family)
  if (!match) return null
  const token = match[1]
  return NON_SCRIPT_NOTO_TOKENS.has(token) ? null : token
}

export const regionForFamily = (family: string): string | null => {
  const token = notoScriptToken(family)
  if (token === null) return null
  return SCRIPT_TO_REGION[token] ?? null
}

const byName = (a: string, b: string): number => a.localeCompare(b)

export const groupFontFamilies = (
  families: readonly string[],
): GroupedFonts => {
  const main: string[] = []
  const byRegion = new Map<string, string[]>()
  for (const family of families) {
    const region = regionForFamily(family)
    if (region === null) {
      main.push(family)
    } else {
      const bucket = byRegion.get(region)
      if (bucket) bucket.push(family)
      else byRegion.set(region, [family])
    }
  }
  const knownInOrder = FONT_REGION_ORDER.filter((r) => byRegion.has(r))
  const extras = [...byRegion.keys()]
    .filter((r) => !FONT_REGION_ORDER.includes(r as never))
    .sort(byName)
  const regions = [...knownInOrder, ...extras].map((region) => ({
    region,
    families: byRegion.get(region)!.slice().sort(byName),
  }))
  return { main: main.slice().sort(byName), regions }
}
